import { ColumnDef } from '@tanstack/react-table'
import { GetInactivePlayerTableData } from '@/modules/manage-player/type'
import { Badge } from '@/common/ui/badge'
import { formatDistanceStrict } from 'date-fns'
import { th } from 'date-fns/locale'
import { Button } from '@/common/ui/button'
import { Link } from 'react-router-dom'
import { SortingArrows } from '@/common/components/SortingArrows'
import { PlayerTagsCell } from '@/common/components/PlayerTagsCell'
import { PhoneCell } from '@/common/components/PhoneCell'
import { UsernameCell } from '@/common/components/UsernameCell'
import AssignPlayerTagsModal from '@/modules/manage-player/components/GetPlayerList/components/GetPlayerListTable/CellAction/components/AssignPlayerTagsModal'
import type { PlayerTagResponse } from '@/modules/tags/types'

export const columns = (
	date: Date,
): ColumnDef<GetInactivePlayerTableData>[] => [
	{
		accessorKey: 'username',
		header: 'ข้อมูลผู้เล่น',
		cell: ({ row }) => {
			const { id, username, phone, tags } = row.original
			return (
				<div className="space-y-1">
					<div className="block">
						<UsernameCell username={username} />
					</div>
					<div className="block">
						<PhoneCell playerId={id} phone={phone} />
					</div>
					<div className="block">
						<PlayerTagsCell tags={tags || []} />
					</div>
					<AssignPlayerTagsModal
						playerId={id}
						playerPhone={phone}
						currentTags={tags as unknown as PlayerTagResponse[]}
					/>
				</div>
			)
		},
	},
	{
		accessorKey: 'totalDepositAmount',
		header: ({ column }) => {
			return (
				<Button
					variant="ghost"
					onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					className="px-0 hover:bg-transparent"
				>
					ยอดเงินฝากทั้งหมด
					<SortingArrows sortOrder={column.getIsSorted()} />
				</Button>
			)
		},
		cell: ({ row }) => (
			<div className="space-y-1 text-sm">
				<p>
					ยอดฝาก:{' '}
					{new Intl.NumberFormat('th-TH').format(
						row.original.totalDepositAmount,
					)}
				</p>
				<p className="text-muted-foreground">
					จำนวนครั้ง:{' '}
					{new Intl.NumberFormat('th-TH').format(row.original.totalDepositTime)}
				</p>
				<p className="text-muted-foreground">
					ฝากล่าสุด:{' '}
					{row.original?.lastDepositAt
						? formatDistanceStrict(date, new Date(row.original.lastDepositAt), {
								locale: th,
								addSuffix: true,
							}).replace('ใน ', '')
						: '-'}
				</p>
			</div>
		),
	},
	{
		accessorKey: 'createdAt',
		header: 'สถานะ/เวลา',
		cell: ({ row }) => {
			const distance = formatDistanceStrict(
				date,
				new Date(row.original.createdAt),
				{
					locale: th,
					addSuffix: true,
				},
			)

			return (
				<div className="space-y-1">
					<Badge variant={row.original.isBanned ? 'destructive' : 'default'}>
						{row.original.isBanned ? 'แบน' : 'ไม่แบน'}
					</Badge>
					<p className="text-xs text-muted-foreground">
						สมัครเมื่อ: {distance.replace('ใน ', '')}
					</p>
					<p className="text-xs text-muted-foreground">
						ออนไลน์ล่าสุด:{' '}
						{row.original.lastOnline
							? new Date(row.original.lastOnline).toLocaleDateString('th-TH', {
									year: 'numeric',
									month: '2-digit',
									day: '2-digit',
								})
							: '-'}
					</p>
				</div>
			)
		},
	},
	{
		id: 'details',
		header: 'การดำเนินการ',
		cell: ({ row }) => (
			<Button asChild variant="outline">
				<Link to={`/operator/get-player-list/player-detail/${row.original.id}`}>
					ดูรายละเอียด
				</Link>
			</Button>
		),
	},
]
